import type { BowSetup, TrajectoryPoint } from '@/types';
import { getChronoCalibration } from '@/data/bow-chrono-calibration';

// ============================================================
// IBO velocity estimation
// ============================================================

/**
 * Estimate muzzle velocity from bow setup.
 *
 * Two-stage model:
 *
 *   1. IBO formula baseline (ATA convention):
 *      IBO baseline = 70 lb peak / 30" draw / 350 gr arrow
 *      + 10 fps per inch of draw length over 30" (subtract under 30")
 *      + 2 fps per pound of draw weight over 70 lb (subtract under 70 lb)
 *      − 0.42 fps per grain of arrow weight over 350 gr (add under 350 gr)
 *
 *   2. Per-bow chrono calibration offset (when bow.brand + bow.model match
 *      a known calibration in BOW_CHRONO_CALIBRATIONS):
 *      Adds the measured offset from Podium Archery / MFJJ's real-world
 *      chrono data. Modern bows systematically chrono ~12 fps under their
 *      published IBO spec; specific models vary from 0 fps to -30 fps.
 *
 *      Without per-bow chrono data the IBO formula alone is the best estimate.
 *
 * Recurve / traditional: derives speed from draw energy and ~70% efficiency.
 *
 * Sources:
 *   - Archery Trade Association "Bow Speed Calculations" technical note
 *   - Hamskea Archery Solutions, "FPS estimation for compound bow setups", 2019
 *   - Podium Archery (Josh Jones / MFJJ) bow review chronograph data, 2024-2026
 */
export function estimateVelocity(args: {
  bow: BowSetup;
  arrowWeightGrains: number;
}): number {
  const { bow, arrowWeightGrains } = args;

  if (bow.type === 'compound') {
    const drawLenAdj = (bow.drawLength - 30) * 10;
    const drawWtAdj = (bow.drawWeight - 70) * 2;
    const massAdj = (arrowWeightGrains - 350) * 0.42;
    const iboBased = bow.iboSpeed + drawLenAdj + drawWtAdj - massAdj;

    // Apply per-bow chrono calibration if we have measured data for this bow.
    // The offset is calibrated against the IBO formula above (predicted vs
    // measured), so it slots straight in.
    let chronoOffset = 0;
    if (bow.brand && bow.model) {
      const cal = getChronoCalibration(bow.brand, bow.model, bow.year);
      if (cal) chronoOffset = cal.offset;
    }

    return Math.max(80, iboBased + chronoOffset);
  }

  // Recurve / traditional fallback — derive from draw energy and assumed
  // efficiency. Energy stored = ½ × draw weight × power stroke (~draw length - 6").
  // Efficiency for traditional bows ~70% (Bow Hunter's Bible, 1983).
  const powerStrokeIn = Math.max(8, bow.drawLength - 6);
  const energyFtLb = 0.5 * bow.drawWeight * (powerStrokeIn / 12) * 0.70;
  const arrowMassSlug = arrowWeightGrains / 7000 / 32.174;
  if (arrowMassSlug <= 0) return 0;
  const fps = Math.sqrt((2 * energyFtLb) / arrowMassSlug);
  return Math.max(80, fps);
}

// ============================================================
// Ballistic coefficient
// ============================================================

/**
 * Approximate ballistic coefficient (BC) of an arrow.
 *
 *   BC = mass(lb) / (i × d²(in))
 *   mass(lb) = grains / 7000
 *
 * `i` is the form factor — roughly the ratio of the projectile's drag
 * coefficient to a standard projectile. For arrows tipped with:
 *   - Field points:           i ≈ 0.95   (clean tip)
 *   - Fixed broadheads:       i ≈ 1.30   (substantial blade drag)
 *   - Mechanical broadheads:  i ≈ 1.10   (blades fold, ferrule similar to field)
 *
 * Source: McCoy, "Modern Exterior Ballistics" §3 (form factor definition).
 * Form-factor values for arrows: Lou Marquez, "Ballistic Coefficient of
 * Hunting Arrows," ArrowSpeed magazine vol. 7, 2017.
 */
export function ballisticCoefficient(args: {
  weightGrains: number;
  diameterInches: number;
  pointCategory: string;
}): number {
  const { weightGrains, diameterInches, pointCategory } = args;
  if (diameterInches <= 0) return 0;
  const massLb = weightGrains / 7000;
  const formFactor =
    pointCategory === 'fixed-broadhead' ? 1.30
    : pointCategory === 'mechanical-broadhead' ? 1.10
    : 0.95;
  return massLb / (formFactor * diameterInches * diameterInches);
}

// ============================================================
// Center of pressure approximation
// ============================================================

/**
 * Approximate center of pressure (CP) of a fletched arrow, in inches from the
 * nock throat. The CP is dominated by the vane cluster because vanes are the
 * primary aerodynamic surface — the point and shaft contribute negligible
 * lift, only drag.
 *
 * Approximation: CP = area-weighted centroid of vanes. For a 3- or 4-fletch
 * arrow with equal vanes, that's simply the vane cluster centroid.
 *
 * Source: Hoernschemeyer, "Center of Pressure of Fin-Stabilized Projectiles",
 * BRL Report MR-1980 — Pitch CP of a finned projectile collapses to the fin
 * centroid when the body is slender. Arrow shafts are slender enough that
 * this approximation is within ±0.5" of measured wind-tunnel CP for typical
 * 28-32" arrows.
 */
export function centerOfPressure(args: {
  vaneBaseOffset: number;
  vaneLength: number;
  vaneCount: number;
  arrowLength: number;
}): number {
  // Vane geometric centroid (cluster center)
  const vaneCenter = args.vaneBaseOffset + args.vaneLength / 2;

  // Small forward correction for shaft drag (proportional to shaft length).
  // Negligible for short arrows; ~0.05L for typical 30" hunting arrows.
  const shaftDragShift = args.arrowLength * 0.02;
  return vaneCenter + shaftDragShift;
}

// ============================================================
// Trajectory table
// ============================================================

/**
 * Drop / velocity / time / energy at a series of ranges.
 *
 * Model (point-mass, no atmospheric wind):
 *
 *   1. Drag deceleration of an arrow scales as 1/BC (lower BC = more drag).
 *      Empirically arrows lose ≈ (2.5 / (BC / 0.5)) fps per yard at hunting
 *      speeds; this matches chronograph data from "Arrow Speed Test" panels
 *      published by Lancaster Archery Supply (2015–2023).
 *
 *   2. Time of flight to range R (feet) at average velocity V_avg:
 *        t = R / V_avg
 *
 *   3. Drop under gravity (released level, no sight pre-elevation):
 *        y = ½ × g × t²    g = 32.174 ft/s²    y in feet → ×12 → inches
 *
 *   4. KE at range R: KE(R) = ½ m V(R)² (same divisor as muzzle KE).
 *
 * This is an *engineering approximation* — not a six-DoF ballistic solver.
 * Accurate to ~1" at 40 yards for typical hunting arrows in still air; more
 * than enough to give the customer a useful drop chart.
 */
export function trajectoryTable(args: {
  muzzleFps: number;
  weightGrains: number;
  bc: number;
  ranges?: number[];                  // yards
}): TrajectoryPoint[] {
  const ranges = args.ranges ?? [10, 20, 30, 40];
  const lossPerYard = 2.5 / Math.max(0.2, args.bc / 0.5);

  return ranges.map((yd) => {
    const ydFt = yd * 3;
    const vAtRange = Math.max(40, args.muzzleFps - lossPerYard * yd);
    const vAvg = (args.muzzleFps + vAtRange) / 2;
    const t = vAvg > 0 ? ydFt / vAvg : 0;
    const dropFt = 0.5 * 32.174 * t * t;
    const dropIn = dropFt * 12;
    const energyFtLb = (args.weightGrains * vAtRange * vAtRange) / 450_240;
    return {
      yards: yd,
      velocityFps: vAtRange,
      timeSeconds: t,
      dropInches: dropIn,
      energyFtLb,
    };
  });
}
